import { useEffect, useState } from 'react';
import { cardStyle, primaryButtonStyle } from '@kilat/ui';

import { useI18n } from '../lib/use-i18n.js';
import { logger } from '../lib/logger.js';
import {
  BOOKING_SETTINGS_DOC_ID,
  bookingSettingsRepoFor,
} from '../lib/repositories/booking-settings-repo.js';
import {
  DEFAULT_APPOINTMENT_PAYMENT_POLICY,
  freeCancelDeadlineMs,
  money,
  requiredPrepaymentSen,
  type AppointmentPaymentPolicy,
} from '@kilat/shared';

/**
 * Booking deposits — the operator decides whether an appointment costs anything
 * up front (SPEC D10).
 *
 * Why this screen exists at all: an appointment holds a bay slot, and a free
 * booking lets a customer hold one and never arrive at no cost to them. Whether
 * that is worth asking customers for money over is a judgement about their own
 * regulars, so it is a setting with a safe default of OFF — never a constant in
 * the server, and never a decision made for them.
 *
 * The copy leads with the consequence rather than the mechanism. An operator
 * does not think "deposit mode, 20 percent"; they think "if someone books and
 * doesn't show up, do I keep anything". The live example below the controls
 * spells out the actual ringgit on a real wash price, because a percentage is
 * an abstraction and the number they will have to defend to a customer is not.
 */

/** A representative wash price for the worked example, in sen (RM 35.00). */
const EXAMPLE_WASH_SEN = 3_500;

interface Props {
  readonly tenantId: string;
}

type Stage =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error' };

export function BookingDepositCard({ tenantId }: Props) {
  const { t } = useI18n();
  const [policy, setPolicy] = useState<AppointmentPaymentPolicy>(
    DEFAULT_APPOINTMENT_PAYMENT_POLICY,
  );
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const doc = await bookingSettingsRepoFor(tenantId).get(BOOKING_SETTINGS_DOC_ID);
        if (cancelled) return;
        setPolicy(doc?.appointmentPayment ?? DEFAULT_APPOINTMENT_PAYMENT_POLICY);
        setStage({ kind: 'ready' });
      } catch (e) {
        logger.error('booking settings load failed', { tenantId, error: String(e) });
        if (!cancelled) setStage({ kind: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  async function save(next: AppointmentPaymentPolicy) {
    setPolicy(next);
    setStage({ kind: 'saving' });
    try {
      await bookingSettingsRepoFor(tenantId).set(BOOKING_SETTINGS_DOC_ID, {
        appointmentPayment: next,
      });
      setStage({ kind: 'saved' });
      logger.info('booking deposit policy saved', { tenantId, mode: next.mode });
    } catch (e) {
      logger.error('booking settings save failed', { tenantId, error: String(e) });
      setStage({ kind: 'error' });
    }
  }

  if (stage.kind === 'loading') {
    return (
      <section style={{ ...cardStyle, marginTop: 'var(--space-4)' }}>
        <p style={mutedStyle}>{t('dashboard.bookingDeposit.loading')}</p>
      </section>
    );
  }

  const upfront = requiredPrepaymentSen(policy, EXAMPLE_WASH_SEN);
  const isOn = policy.mode !== 'none';

  return (
    <section style={{ ...cardStyle, marginTop: 'var(--space-4)' }}>
      <header>
        <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t('dashboard.bookingDeposit.title')}
        </h2>
        <p style={{ ...mutedStyle, margin: 'var(--space-1) 0 0 0' }}>
          {t('dashboard.bookingDeposit.description')}
        </p>
      </header>

      <div style={{ display: 'grid', gap: 'var(--space-3)', marginTop: 'var(--space-5)' }}>
        {(['none', 'deposit', 'full'] as const).map((mode) => (
          <label
            key={mode}
            style={{
              display: 'flex',
              gap: 'var(--space-3)',
              alignItems: 'flex-start',
              padding: 'var(--space-4)',
              borderRadius: 'var(--radius-md)',
              border: `1px solid ${policy.mode === mode ? 'var(--color-primary)' : 'var(--color-border)'}`,
              background: policy.mode === mode ? 'var(--color-primary-soft)' : 'transparent',
              cursor: 'pointer',
            }}
          >
            <input
              type="radio"
              name="appointment-payment-mode"
              checked={policy.mode === mode}
              onChange={() => void save({ ...policy, mode })}
              style={{ marginTop: '0.2em' }}
            />
            <span>
              <span style={{ display: 'block', fontWeight: 600 }}>
                {t(`dashboard.bookingDeposit.mode.${mode}.label`)}
              </span>
              <span style={{ ...mutedStyle, display: 'block', marginTop: 'var(--space-1)' }}>
                {t(`dashboard.bookingDeposit.mode.${mode}.help`)}
              </span>
            </span>
          </label>
        ))}
      </div>

      {isOn ? (
        <div style={{ display: 'grid', gap: 'var(--space-4)', marginTop: 'var(--space-5)' }}>
          {policy.mode === 'deposit' ? (
            <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
              <span style={{ fontSize: 'var(--text-meta)', fontWeight: 600 }}>
                {t('dashboard.bookingDeposit.percentLabel')}
              </span>
              <input
                type="number"
                min={1}
                max={100}
                value={policy.depositPercent}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (Number.isInteger(v) && v >= 1 && v <= 100) {
                    void save({ ...policy, depositPercent: v });
                  }
                }}
                style={inputStyle}
              />
            </label>
          ) : null}

          <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <span style={{ fontSize: 'var(--text-meta)', fontWeight: 600 }}>
              {t('dashboard.bookingDeposit.freeCancelLabel')}
            </span>
            <input
              type="number"
              min={0}
              max={168}
              value={policy.freeCancelHours}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isInteger(v) && v >= 0 && v <= 168) {
                  void save({ ...policy, freeCancelHours: v });
                }
              }}
              style={inputStyle}
            />
            {/*
              Spelled out because the direction is genuinely counter-intuitive:
              a BIGGER number is stricter, and 0 means free right up to the
              appointment. The first draft of the schema comment got this
              backwards, so an operator certainly can.
            */}
            <span style={mutedStyle}>
              {policy.freeCancelHours === 0
                ? t('dashboard.bookingDeposit.freeCancelAlways')
                : t('dashboard.bookingDeposit.freeCancelHelp', {
                    hours: String(policy.freeCancelHours),
                  })}
            </span>
          </label>

          {/*
            The worked example. A percentage is an abstraction; the ringgit an
            operator will have to justify to a customer standing in front of
            them is not.
          */}
          <p
            style={{
              margin: 0,
              padding: 'var(--space-4)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-sunken)',
              fontSize: 'var(--text-meta)',
              lineHeight: 1.6,
            }}
          >
            {t('dashboard.bookingDeposit.example', {
              wash: money.format(EXAMPLE_WASH_SEN),
              upfront: money.format(upfront),
              rest: money.format(Math.max(0, EXAMPLE_WASH_SEN - upfront)),
            })}
            <br />
            {t('dashboard.bookingDeposit.exampleNoShow', { upfront: money.format(upfront) })}
          </p>
        </div>
      ) : null}

      <p style={{ ...mutedStyle, marginTop: 'var(--space-4)' }} aria-live="polite">
        {stage.kind === 'saving' ? t('dashboard.bookingDeposit.saving') : null}
        {stage.kind === 'saved' ? t('dashboard.bookingDeposit.saved') : null}
        {stage.kind === 'error' ? (
          <span style={{ color: 'var(--color-danger)' }}>
            {t('dashboard.bookingDeposit.saveError')}{' '}
            <button
              type="button"
              onClick={() => void save(policy)}
              style={{ ...primaryButtonStyle, padding: '0.25em 0.75em' }}
            >
              {t('dashboard.bookingDeposit.retry')}
            </button>
          </span>
        ) : null}
      </p>
    </section>
  );
}

const mutedStyle = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.5,
} as const;

const inputStyle = {
  padding: '0.5em 0.75em',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  fontSize: 'var(--text-body)',
  maxWidth: '10rem',
  minHeight: 'var(--touch-target)',
} as const;

/** Exported for the worked example in tests. */
export { EXAMPLE_WASH_SEN, freeCancelDeadlineMs };
